import React, { useRef, useState } from 'react';
import { UploadCloud, Download, FileSpreadsheet, Star, Play } from 'lucide-react';
import { toastService } from '../../../services/toastService';
import { migrationApi, putFile, type MigrationJob } from '../../../services/migrationApi';
import { Btn, Card, Banner, ANALYSABLE, RUBY, fmtBytes, fmtDateTime, toastApiError } from './shared';

const guessType = (f: File): string => {
  if (f.type) return f.type;
  const ext = f.name.split('.').pop()?.toLowerCase();
  if (ext === 'csv') return 'text/csv';
  if (ext === 'xlsx') return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  if (ext === 'xls') return 'application/vnd.ms-excel';
  return 'application/octet-stream';
};

/** Open a presigned download for one of this job's S3 keys. */
export async function openDownload(job: MigrationJob, key: string) {
  try {
    const { url } = await migrationApi.downloadUrl(job.tenantId, job.jobId, key);
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  } catch (e) {
    toastApiError(e, 'Could not download the file');
  }
}

type Uploading = { name: string; progress: number; error?: string };

export const FilesStep: React.FC<{ job: MigrationJob; reload: () => Promise<void> | void; onJob: (j: MigrationJob) => void }> = ({ job, reload, onJob }) => {
  const [uploads, setUploads] = useState<Uploading[]>([]);
  const [drag, setDrag] = useState(false);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [analysing, setAnalysing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const canUpload = ANALYSABLE.includes(job.status);
  const files = job.files || [];
  const mainKey = job.mainFileKey;

  const uploadAll = async (list: FileList | File[]) => {
    const arr = Array.from(list);
    if (!arr.length) return;
    if (!canUpload) { toastService.warning(`Files can't be added while the job is ${job.status.replace(/_/g, ' ')}`); return; }
    setUploads((prev) => [...prev, ...arr.map((f) => ({ name: f.name, progress: 0 }))]);
    const setOne = (name: string, patch: Partial<Uploading>) =>
      setUploads((prev) => prev.map((u) => (u.name === name ? { ...u, ...patch } : u)));
    let ok = 0;
    for (const f of arr) {
      try {
        const p = await migrationApi.presignUpload(job.tenantId, job.jobId, { filename: f.name, size: f.size, contentType: guessType(f) });
        await putFile(p.uploadUrl, f, p.contentType, (x) => setOne(f.name, { progress: x }));
        setOne(f.name, { progress: 1 });
        ok += 1;
      } catch (e: any) {
        setOne(f.name, { error: e?.message || 'Upload failed' });
        toastApiError(e, `Could not upload ${f.name}`);
      }
    }
    if (ok) toastService.success(`${ok} file${ok === 1 ? '' : 's'} uploaded`);
    await reload();
    setUploads((prev) => prev.filter((u) => u.error));
  };

  const makeMain = async (key: string) => {
    setBusyKey(key);
    try {
      // A different main file means different columns: send an empty column map
      // so the next analysis re-detects the layout for that file.
      const base = { source: job.mapping?.source || job.source || 'other', stockColumns: {}, priceColumns: {}, locationMap: {} };
      const mapping = key === mainKey && job.mapping ? job.mapping : { ...base, columns: {} };
      const r = await migrationApi.saveMapping(job.tenantId, job.jobId, mapping, key);
      onJob(r.job);
      toastService.success('Main file set — analyse to read it');
    } catch (e) {
      toastApiError(e, 'Could not set the main file');
    } finally {
      setBusyKey(null);
    }
  };

  const analyse = async () => {
    setAnalysing(true);
    try {
      const r = await migrationApi.analyse(job.tenantId, job.jobId);
      onJob(r.job);
    } catch (e) {
      toastApiError(e, 'Could not start the analysis');
    } finally {
      setAnalysing(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card
        title={<span className="flex items-center gap-2"><FileSpreadsheet size={16} className="text-stone-500" />Files</span>}
        actions={<Btn kind="black" icon={<Play size={16} />} busy={analysing} disabled={!canUpload || !files.length} onClick={analyse}>Analyse</Btn>}
      >
        {job.notes && <div className="mb-3"><Banner tone="sky"><b>Client notes:</b> {job.notes}</Banner></div>}
        {files.length === 0 ? (
          <p className="text-sm text-stone-400">No files yet. Upload the export below, or turn on the client's upload page.</p>
        ) : (
          <div className="-mx-5 divide-y divide-stone-100 border-y border-stone-100">
            {files.map((f) => {
              const isMain = f.key === mainKey;
              return (
                <div key={f.key} className="px-5 py-2 flex items-center gap-3 min-h-[56px] flex-wrap">
                  <FileSpreadsheet size={18} className="text-stone-400 shrink-0" />
                  <div className="flex-1 min-w-[180px]">
                    <div className="font-medium text-stone-900 truncate flex items-center gap-2">
                      {f.name}
                      {isMain && <span className="text-[10px] font-bold uppercase tracking-wider text-white rounded-full px-2 py-0.5" style={{ background: RUBY }}>Main</span>}
                    </div>
                    <div className="text-xs text-stone-400">{fmtBytes(f.size)} · {fmtDateTime(f.uploadedAt)}{f.uploadedBy ? ` · ${f.uploadedBy}` : ''}</div>
                  </div>
                  {!isMain && (
                    <Btn icon={<Star size={14} />} busy={busyKey === f.key} disabled={!canUpload || !!busyKey} onClick={() => makeMain(f.key)}>Use as main file</Btn>
                  )}
                  <Btn icon={<Download size={14} />} onClick={() => openDownload(job, f.key)} aria-label={`Download ${f.name}`}>Download</Btn>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <div
        role="button"
        tabIndex={0}
        aria-disabled={!canUpload}
        onClick={() => canUpload && inputRef.current?.click()}
        onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && canUpload) inputRef.current?.click(); }}
        onDragOver={(e) => { e.preventDefault(); if (canUpload) setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); uploadAll(e.dataTransfer.files); }}
        className={`rounded-xl border-2 border-dashed p-8 text-center transition ${canUpload ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'} ${drag ? 'bg-rose-50' : 'bg-white hover:bg-stone-50'}`}
        style={{ borderColor: drag ? RUBY : '#d6d3d1' }}
      >
        <UploadCloud size={28} className="mx-auto" style={{ color: RUBY }} />
        <div className="mt-2 font-medium text-stone-800">Drop export files here, or click to choose</div>
        <div className="text-xs text-stone-400 mt-1">CSV or Excel (.xlsx). Files go straight to secure storage — no size limit.</div>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept=".csv,.xlsx,.xls,.txt,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
          className="hidden"
          onChange={(e) => { if (e.target.files) uploadAll(e.target.files); e.target.value = ''; }}
        />
      </div>

      {uploads.length > 0 && (
        <div className="bg-white rounded-xl border border-stone-200 p-4 space-y-3">
          {uploads.map((u) => (
            <div key={u.name}>
              <div className="flex items-center justify-between text-sm">
                <span className="truncate text-stone-800">{u.name}</span>
                <span className={u.error ? 'text-rose-700 text-xs' : 'text-stone-500 text-xs'}>{u.error || `${Math.round(u.progress * 100)}%`}</span>
              </div>
              <div className="h-2 rounded-full bg-stone-100 mt-1 overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${Math.round(u.progress * 100)}%`, background: u.error ? '#e11d48' : RUBY }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FilesStep;
